import React, { useRef, useEffect } from 'react'
import { useGetContentImageQuery, useViewContentQuery } from '../../services/contentApi'
import { useAppSelector } from '../../store/hooks'
// PERFORMANCE: Removed selectAllBoundingBoxes import - no longer needed
import { selectSelectedProjectId } from '../../store/slices/projectSlice'
import { useGetTagsQuery } from '../../services/tagApi'
// import { getContrastYIQ } from '../../utils'
import { useGetProjectQuery } from '../../services/projectsApi'
import { AnnotationDataBoundingBox } from '../../types/models/annotation'
import { migrateBoundingBoxToPoints, getMinMaxFromPoints } from '../../utils/helpers'

interface AALSnippetProps {
  contentId: string
  bbox: AnnotationDataBoundingBox
  style?: React.CSSProperties
  rotateLongSideVertical?: boolean
  paddingMultiplier?: number // Optional padding override
}

const AALSnippet: React.FC<AALSnippetProps> = ({
  contentId,
  bbox,
  style,
  rotateLongSideVertical = false,
  paddingMultiplier,
}) => {
  // PERFORMANCE DEBUG: Uncomment to see when snippets re-render
  // console.log(`[AALSnippet] Render for class_index: ${bbox.class_index}`)

  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project, isLoading: isProjectLoading } = useGetProjectQuery(
    selectedProjectId || '',
    {
      skip: !selectedProjectId,
    }
  )
  // PERFORMANCE: Removed allBoundingBoxes selector - we don't need to watch all boxes
  // This was causing all snippets to re-render whenever any box changed
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [imageCacheTimestamp, setImageCacheTimestamp] = React.useState<number>(Date.now())

  // Listen for annotation updates to refresh images
  React.useEffect(() => {
    const handleAnnotationUpdate = () => {
      setImageCacheTimestamp(Date.now())
    }

    window.addEventListener('annotation-updated', handleAnnotationUpdate)
    window.addEventListener('reset-image-loading', handleAnnotationUpdate)

    return () => {
      window.removeEventListener('annotation-updated', handleAnnotationUpdate)
      window.removeEventListener('reset-image-loading', handleAnnotationUpdate)
    }
  }, [])

  const { data: imageUrl } = useGetContentImageQuery(
    {
      ContentID: contentId,
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
      _t: imageCacheTimestamp,
    },
    {
      // Skip if missing essential data or if project doesn't have datasetid (similar to AnnotationsReview pattern)
      skip: !contentId || !selectedProjectId || !project?.datasetid,
    }
  )

  const { data: tags } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  // Fetch content data to get the original dimensions used for coordinate conversion
  const { data: contentData } = useViewContentQuery(
    {
      ContentID: contentId || '',
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !contentId || !selectedProjectId || !project?.datasetid }
  )

  useEffect(() => {
    // PERFORMANCE FIX: Don't check allBoundingBoxes array - just render the bbox we have
    // This prevents re-renders when other boxes are being edited
    if (!imageUrl || !bbox) {
      return
    }

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = imageUrl

    img.onload = () => {
      const canvas = canvasRef.current
      if (!canvas) return

      const ctx = canvas.getContext('2d')
      if (!ctx) return

      // Get dimensions: content dimensions from API metadata, natural dimensions from loaded image
      const naturalWidth = img.naturalWidth
      const naturalHeight = img.naturalHeight
      const contentWidth = contentData?.width || naturalWidth
      const contentHeight = contentData?.height || naturalHeight

      // Calculate scale factors to transform from content to natural dimensions
      // Bbox coordinates are in content space, but we need to draw on the natural image
      const contentToNaturalX = naturalWidth / contentWidth
      const contentToNaturalY = naturalHeight / contentHeight

      const tag = tags?.tags?.find((t) => t.id === bbox.name)
      const tagColor = tag
        ? `#${tag?.color.toString(16).padStart(6, '0').slice(0, 6)}`
        : 'rgba(255, 0, 0, 0.8)'

      // Calculate the center and dimensions of the bounding box in pixel coordinates
      let bboxCenterX: number, bboxCenterY: number, bboxWidth: number, bboxHeight: number
      let pixelPoints: number[] | null = null // Store scaled pixel points for later use

      if (bbox.points && bbox.points.length === 8) {
        // Check if points are normalized (0-1) or in pixel coordinates
        const maxX = Math.max(bbox.points[0], bbox.points[2], bbox.points[4], bbox.points[6])
        const maxY = Math.max(bbox.points[1], bbox.points[3], bbox.points[5], bbox.points[7])
        const areNormalized = maxX <= 1 && maxY <= 1 && (maxX > 0 || maxY > 0)

        if (areNormalized) {
          // Normalized [0–1] → content pixels
          pixelPoints = [
            bbox.points[0] * contentWidth,
            bbox.points[1] * contentHeight,
            bbox.points[2] * contentWidth,
            bbox.points[3] * contentHeight,
            bbox.points[4] * contentWidth,
            bbox.points[5] * contentHeight,
            bbox.points[6] * contentWidth,
            bbox.points[7] * contentHeight,
          ]
        } else {
          // Points are in content pixel coordinates - convert to natural space for drawing
          const contentPoints = bbox.points as number[]
          pixelPoints = [
            contentPoints[0] * contentToNaturalX,
            contentPoints[1] * contentToNaturalY,
            contentPoints[2] * contentToNaturalX,
            contentPoints[3] * contentToNaturalY,
            contentPoints[4] * contentToNaturalX,
            contentPoints[5] * contentToNaturalY,
            contentPoints[6] * contentToNaturalX,
            contentPoints[7] * contentToNaturalY,
          ]
        }

        // Calculate center and dimensions from pixel coordinates
        const xCoords = [pixelPoints[0], pixelPoints[2], pixelPoints[4], pixelPoints[6]]
        const yCoords = [pixelPoints[1], pixelPoints[3], pixelPoints[5], pixelPoints[7]]
        bboxCenterX = (Math.min(...xCoords) + Math.max(...xCoords)) / 2
        bboxCenterY = (Math.min(...yCoords) + Math.max(...yCoords)) / 2

        // Get axis-aligned bounding rectangle for zoom calculation
        const { xmin, ymin, xmax, ymax } = getMinMaxFromPoints(pixelPoints)
        bboxWidth = xmax - xmin
        bboxHeight = ymax - ymin
      } else {
        // Legacy format: xmin/ymin/xmax/ymax
        // After API transform, these are in content pixel space
        let xmin = bbox.xmin ?? 0
        let xmax = bbox.xmax ?? 0
        let ymin = bbox.ymin ?? 0
        let ymax = bbox.ymax ?? 0

        // Detect if normalized (shouldn't happen after API transform, but handle for safety)
        const maybeNormalized = xmax <= 1 && ymax <= 1 && (xmax > 0 || ymax > 0)

        if (maybeNormalized) {
          // Convert from normalized [0,1] to content pixels
          xmin = xmin * contentWidth
          xmax = xmax * contentWidth
          ymin = ymin * contentHeight
          ymax = ymax * contentHeight
        }
        // Convert from content pixel space to natural pixel space
        xmin = xmin * contentToNaturalX
        xmax = xmax * contentToNaturalX
        ymin = ymin * contentToNaturalY
        ymax = ymax * contentToNaturalY

        bboxCenterX = (xmin + xmax) / 2
        bboxCenterY = (ymin + ymax) / 2
        bboxWidth = xmax - xmin
        bboxHeight = ymax - ymin
      }

      // Calculate the zoom factor needed to make the bounding box content fit the canvas
      // Add padding to ensure the entire bounding box (with border) is visible
      // Use custom padding if provided, otherwise use defaults
      const defaultPadding = rotateLongSideVertical ? 3.0 : 2.8
      const padding = paddingMultiplier !== undefined ? paddingMultiplier : defaultPadding
      const zoomX = canvas.width / (bboxWidth * padding)
      const zoomY = canvas.height / (bboxHeight * padding)
      const zoom = Math.min(zoomX, zoomY)

      // Calculate the translation needed to center the bounding box
      const translateX = canvas.width / 2 - bboxCenterX * zoom
      const translateY = canvas.height / 2 - bboxCenterY * zoom

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Apply transformations
      ctx.save()
      // If rotate is requested (OBB), rotate the image about the bbox center first
      if (rotateLongSideVertical && pixelPoints && pixelPoints.length === 8) {
        // Use the already-scaled pixelPoints from above
        const rotationPixelPoints = pixelPoints
        const xCoords = [
          rotationPixelPoints[0],
          rotationPixelPoints[2],
          rotationPixelPoints[4],
          rotationPixelPoints[6],
        ]
        const yCoords = [
          rotationPixelPoints[1],
          rotationPixelPoints[3],
          rotationPixelPoints[5],
          rotationPixelPoints[7],
        ]
        const centerX = (Math.min(...xCoords) + Math.max(...xCoords)) / 2
        const centerY = (Math.min(...yCoords) + Math.max(...yCoords)) / 2

        // Determine rotation required to make long edge vertical
        const dx = rotationPixelPoints[2] - rotationPixelPoints[0]
        const dy = rotationPixelPoints[3] - rotationPixelPoints[1]
        const topEdgeLen = Math.hypot(dx, dy)
        const leftDx = rotationPixelPoints[6] - rotationPixelPoints[0]
        const leftDy = rotationPixelPoints[7] - rotationPixelPoints[1]
        const leftEdgeLen = Math.hypot(leftDx, leftDy)
        const angle = Math.atan2(dy, dx)
        let rotation = topEdgeLen >= leftEdgeLen ? Math.PI / 2 - angle : -angle

        // Compute rotated coordinates relative to center to assess top-most corner
        const cos = Math.cos(rotation)
        const sin = Math.sin(rotation)
        const relPoints: Array<{ rx: number; ry: number }> = []
        for (let i = 0; i < rotationPixelPoints.length; i += 2) {
          const rx =
            (rotationPixelPoints[i] - centerX) * cos - (rotationPixelPoints[i + 1] - centerY) * sin
          const ry =
            (rotationPixelPoints[i] - centerX) * sin + (rotationPixelPoints[i + 1] - centerY) * cos
          relPoints.push({ rx, ry })
        }
        let minIdx = 0
        for (let i = 1; i < 4; i++) {
          if (relPoints[i].ry < relPoints[minIdx].ry) minIdx = i
        }
        // Ensure point 1 (index 0) is the top-most by flipping 180deg if needed
        if (minIdx !== 0) {
          rotation += Math.PI
          const cos2 = Math.cos(Math.PI)
          const sin2 = Math.sin(Math.PI)
          for (let i = 0; i < relPoints.length; i++) {
            const x0 = relPoints[i].rx
            const y0 = relPoints[i].ry
            relPoints[i].rx = x0 * cos2 - y0 * sin2
            relPoints[i].ry = x0 * sin2 + y0 * cos2
          }
        }

        // Effective dimensions from rotated extents to avoid clipping
        let minRx = relPoints[0].rx
        let maxRx = relPoints[0].rx
        let minRy = relPoints[0].ry
        let maxRy = relPoints[0].ry
        for (let i = 1; i < 4; i++) {
          if (relPoints[i].rx < minRx) minRx = relPoints[i].rx
          if (relPoints[i].rx > maxRx) maxRx = relPoints[i].rx
          if (relPoints[i].ry < minRy) minRy = relPoints[i].ry
          if (relPoints[i].ry > maxRy) maxRy = relPoints[i].ry
        }
        const effWidth = maxRx - minRx
        const effHeight = maxRy - minRy
        const zoomXr = canvas.width / (effWidth * padding)
        const zoomYr = canvas.height / (effHeight * padding)
        const zoomR = Math.min(zoomXr, zoomYr)
        const marginCanvasY = Math.max(0, (canvas.height - effHeight * zoomR) / 2)
        // Compute current top Y in canvas space and needed delta to align to margin
        const yMinCanvas = canvas.height / 2 + zoomR * minRy
        const deltaCanvasY = marginCanvasY - yMinCanvas
        const deltaImageY = deltaCanvasY / zoomR

        // Build combined transform: center -> rotate -> scale -> translate to center -> shift top to margin
        ctx.translate(canvas.width / 2, canvas.height / 2)
        ctx.rotate(rotation)
        ctx.scale(zoomR, zoomR)
        ctx.translate(-centerX, -centerY)
        ctx.translate(0, deltaImageY)
        // Draw image at natural size (bbox coords already converted to natural space)
        ctx.drawImage(img, 0, 0)
        ctx.restore()
        ctx.save()
        // After drawing rotated image, prepare to draw overlay points in the same transformed space
        ctx.translate(canvas.width / 2, canvas.height / 2)
        ctx.rotate(rotation)
        ctx.scale(zoomR, zoomR)
        ctx.translate(-centerX, -centerY)
        ctx.translate(0, deltaImageY)
        // Keep a reference to current zoom for overlay sizing
        ;(ctx as any)._currentZoom = zoomR
      } else {
        ctx.translate(translateX, translateY)
        ctx.scale(zoom, zoom)
        // Draw image at natural size (bbox coords already converted to natural space)
        ctx.drawImage(img, 0, 0)
        ctx.restore()
        ctx.save()
        ctx.translate(translateX, translateY)
        ctx.scale(zoom, zoom)
        ;(ctx as any)._currentZoom = zoom
      }

      // Calculate the transformed bounding box coordinates for the snippet
      // Use the already-calculated pixelPoints from above (they're already scaled correctly)
      if (pixelPoints && pixelPoints.length === 8) {
        // pixelPoints is already calculated and scaled above, use it directly

        // Draw the bounding box directly in current transformed space
        // Scale lineWidth inversely with zoom to maintain consistent visual thickness
        // Use a higher value and clamp to minimum to prevent dotted appearance
        const currentZoom = (ctx as any)._currentZoom || zoom
        ctx.strokeStyle = tagColor
        ctx.lineWidth = Math.max(2, 6 / currentZoom)
        ctx.beginPath()
        ctx.moveTo(pixelPoints[0], pixelPoints[1]) // top-left
        ctx.lineTo(pixelPoints[2], pixelPoints[3]) // top-right
        ctx.lineTo(pixelPoints[4], pixelPoints[5]) // bottom-right
        ctx.lineTo(pixelPoints[6], pixelPoints[7]) // bottom-left
        ctx.closePath()
        ctx.stroke()

        // Indicators removed
      } else {
        // Fallback for boxes without points - coordinates are in content pixel space
        let xmin = bbox.xmin ?? 0
        let xmax = bbox.xmax ?? 0
        let ymin = bbox.ymin ?? 0
        let ymax = bbox.ymax ?? 0

        // If values are between 0-1, assume they're normalized and convert to content pixels
        if (xmax <= 1 && ymax <= 1 && (xmax > 0 || ymax > 0)) {
          xmin = xmin * contentWidth
          xmax = xmax * contentWidth
          ymin = ymin * contentHeight
          ymax = ymax * contentHeight
        }

        // Coordinates are now in current transformed space
        const x1 = xmin
        const y1 = ymin
        const x2 = xmax
        const y2 = ymax

        // Scale lineWidth inversely with zoom to maintain consistent visual thickness
        // Use a higher value and clamp to minimum to prevent dotted appearance
        const currentZoom = (ctx as any)._currentZoom || zoom
        ctx.strokeStyle = tagColor
        ctx.lineWidth = Math.max(2, 6 / currentZoom)
        ctx.strokeRect(x1, y1, x2 - x1, y2 - y1)

        // Indicators removed
      }

      // Snippet thumbnails intentionally omit label text; pills are shown below.

      ctx.restore()
    }
  }, [imageUrl, bbox, tags?.tags, rotateLongSideVertical, contentData])
  // PERFORMANCE: Removed allBoundingBoxes from dependencies to prevent unnecessary re-renders

  return (
    <canvas
      ref={canvasRef}
      width={512}
      height={512}
      style={{
        display: 'block',
        objectFit: 'contain',
        ...style,
      }}
    />
  )
}

// PERFORMANCE: Memoize snippet component to prevent re-renders when parent re-renders
// Only re-render when THIS specific snippet's bbox coordinates actually change
export default React.memo(AALSnippet, (prevProps, nextProps) => {
  // Return TRUE if props are EQUAL (don't re-render)
  // Return FALSE if props changed (do re-render)

  // Different content, rotation setting, or padding = must re-render
  if (
    prevProps.contentId !== nextProps.contentId ||
    prevProps.rotateLongSideVertical !== nextProps.rotateLongSideVertical ||
    prevProps.paddingMultiplier !== nextProps.paddingMultiplier
  ) {
    return false
  }

  // Different class_index = different box entirely = must re-render
  if (prevProps.bbox.class_index !== nextProps.bbox.class_index) {
    return false
  }

  // Check if THIS box's coordinates changed
  const coordsChanged =
    prevProps.bbox.xmin !== nextProps.bbox.xmin ||
    prevProps.bbox.ymin !== nextProps.bbox.ymin ||
    prevProps.bbox.xmax !== nextProps.bbox.xmax ||
    prevProps.bbox.ymax !== nextProps.bbox.ymax

  if (coordsChanged) {
    return false // Coordinates changed, must re-render
  }

  // Check if points changed (for oriented bounding boxes)
  const prevPoints = prevProps.bbox.points
  const nextPoints = nextProps.bbox.points

  if (prevPoints && nextPoints && prevPoints.length === nextPoints.length) {
    // Compare each point value
    for (let i = 0; i < prevPoints.length; i++) {
      if (prevPoints[i] !== nextPoints[i]) {
        return false // Points changed, must re-render
      }
    }
  } else if (prevPoints !== nextPoints) {
    // One has points, the other doesn't, or different lengths
    return false
  }

  // All checks passed - props are equal, don't re-render
  return true
})
